import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { storeCloseAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import Modal from '../../components/common/Modal';

const formatCurrencyAmount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '0.00';
  return numeric.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const seedSellerRows = (sellers = [], closed = false) => (
  (sellers || []).map((row) => ({
    sellerId: row.sellerId,
    sellerName: row.sellerName,
    fmdSystemSales: Number(row.fmdSystemSales) || 0,
    sellerSystemSales: closed
      ? String(Number(row.sellerSystemSales) || 0)
      : '',
  }))
);

const StoreCloseControl = ({ storeInfoId, vendorId, storeName, todayKey }) => {
  const queryClient = useQueryClient();
  const { hasFeatureAccess } = useAuthStore();
  const canClose = hasFeatureAccess('store_close.close');
  const canEditReopen = hasFeatureAccess('store_close.edit');

  const [modalOpen, setModalOpen] = useState(false);
  const [doubleCheckOpen, setDoubleCheckOpen] = useState(false);
  const [requestModalOpen, setRequestModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  const [reopenReason, setReopenReason] = useState('');
  const [sellerRows, setSellerRows] = useState([]);
  const [reviewSellerRows, setReviewSellerRows] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const formSeededRef = useRef(false);

  const closeQueryKey = ['storeCloseStatus', String(storeInfoId || ''), String(vendorId || ''), todayKey];
  const {
    data: closeStatusData,
    isLoading: closeStatusLoading,
    isError: closeStatusError,
    error: closeStatusQueryError,
    refetch: refetchCloseStatus,
  } = useQuery(
    closeQueryKey,
    () => storeCloseAPI.getStatus({
      date: todayKey,
      storeInfoId,
      vendorId,
    }),
    {
      enabled: Boolean(canClose && storeInfoId && vendorId),
      retry: false,
      refetchOnWindowFocus: true,
      refetchInterval: canEditReopen ? 30 * 1000 : false,
      staleTime: 15 * 1000,
    }
  );

  const closeStatus = closeStatusData?.data?.data || closeStatusData?.data || null;
  const isClosed = Boolean(closeStatus?.isClosed);
  const toggleOn = !isClosed;
  const closedByName = String(closeStatus?.closedByName || '').trim();
  const closedAtLabel = closeStatus?.updatedAt
    ? new Date(closeStatus.updatedAt).toLocaleString()
    : '';
  const reopenRequest = closeStatus?.reopenRequest || null;
  const hasPendingReopen = Boolean(reopenRequest?.requested);

  const salesDateLabel = useMemo(() => {
    const [year, month, day] = String(todayKey || '').split('-').map(Number);
    if (!year || !month || !day) return todayKey;
    return new Date(year, month - 1, day).toLocaleDateString(undefined, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }, [todayKey]);

  useEffect(() => {
    if (!modalOpen) {
      formSeededRef.current = false;
      return;
    }
    if (!closeStatus?.sellers?.length) return;
    if (formSeededRef.current) return;
    setSellerRows(seedSellerRows(closeStatus.sellers, Boolean(closeStatus.isClosed)));
    setFormErrors({});
    setSubmitError('');
    formSeededRef.current = true;
  }, [modalOpen, closeStatus]);

  const invalidateClose = async () => {
    await queryClient.invalidateQueries(closeQueryKey);
  };

  const confirmMutation = useMutation(
    (payload) => storeCloseAPI.confirmClose(payload),
    {
      onSuccess: async () => {
        setDoubleCheckOpen(false);
        setModalOpen(false);
        formSeededRef.current = false;
        await invalidateClose();
      },
      onError: (error) => {
        setDoubleCheckOpen(false);
        setSubmitError(error?.response?.data?.message || error.message || 'Failed to close store.');
      },
    }
  );

  const requestMutation = useMutation(
    (payload) => storeCloseAPI.requestReopen(payload),
    {
      onSuccess: async () => {
        setRequestModalOpen(false);
        setReopenReason('');
        setSubmitError('');
        await invalidateClose();
      },
      onError: (error) => {
        setSubmitError(error?.response?.data?.message || error.message || 'Failed to send reopen request.');
      },
    }
  );

  const reviewMutation = useMutation(
    (payload) => storeCloseAPI.reviewReopen(payload),
    {
      onSuccess: async () => {
        setReviewModalOpen(false);
        setSubmitError('');
        await invalidateClose();
      },
      onError: (error) => {
        setSubmitError(error?.response?.data?.message || error.message || 'Failed to review reopen request.');
      },
    }
  );

  const totals = useMemo(() => sellerRows.reduce((acc, row) => {
    const fmd = Number(row.fmdSystemSales) || 0;
    const seller = Number(row.sellerSystemSales);
    const sellerValue = Number.isFinite(seller) ? seller : 0;
    return {
      fmdSystemSales: acc.fmdSystemSales + fmd,
      sellerSystemSales: acc.sellerSystemSales + sellerValue,
      variance: acc.variance + (fmd - sellerValue),
    };
  }, { fmdSystemSales: 0, sellerSystemSales: 0, variance: 0 }), [sellerRows]);

  const reviewTotals = useMemo(() => reviewSellerRows.reduce((acc, row) => {
    const fmd = Number(row.fmdSystemSales) || 0;
    const seller = Number(row.sellerSystemSales);
    const sellerValue = Number.isFinite(seller) ? seller : 0;
    return {
      fmdSystemSales: acc.fmdSystemSales + fmd,
      sellerSystemSales: acc.sellerSystemSales + sellerValue,
      variance: acc.variance + (fmd - sellerValue),
    };
  }, { fmdSystemSales: 0, sellerSystemSales: 0, variance: 0 }), [reviewSellerRows]);

  const openCloseModal = async () => {
    if (!storeInfoId || !vendorId) {
      setSubmitError('Select a store from the header first.');
      setModalOpen(true);
      return;
    }
    formSeededRef.current = false;
    setSellerRows([]);
    setModalOpen(true);
    try {
      const refreshed = await refetchCloseStatus();
      const nextStatus = refreshed?.data?.data?.data || refreshed?.data?.data || null;
      if (nextStatus?.sellers?.length && !formSeededRef.current) {
        setSellerRows(seedSellerRows(nextStatus.sellers, Boolean(nextStatus.isClosed)));
        formSeededRef.current = true;
      }
    } catch (_error) {
      // keep modal open
    }
  };

  const openReviewModal = () => {
    setSubmitError('');
    setReviewSellerRows(seedSellerRows(closeStatus?.sellers || [], true));
    setReviewModalOpen(true);
  };

  const handleToggleClick = () => {
    if (confirmMutation.isLoading || !canClose) return;
    if (isClosed) {
      formSeededRef.current = false;
      setModalOpen(true);
      return;
    }
    openCloseModal();
  };

  const updateSellerSystemSales = (index, value) => {
    const cleaned = String(value ?? '').replace(/[^\d.]/g, '');
    setSellerRows((current) => current.map((item, itemIndex) => (
      itemIndex === index
        ? { ...item, sellerSystemSales: cleaned }
        : item
    )));
  };

  const validateSellerRows = (rows, setErrors) => {
    const errors = {};
    rows.forEach((row, index) => {
      const key = `seller_${index}`;
      if (row.sellerSystemSales === '' || row.sellerSystemSales === null || row.sellerSystemSales === undefined) {
        errors[key] = 'Required';
        return;
      }
      const numeric = Number(row.sellerSystemSales);
      if (!Number.isFinite(numeric) || numeric < 0) {
        errors[key] = 'Enter a valid amount';
      }
    });
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleConfirm = () => {
    setSubmitError('');
    if (isClosed) {
      setModalOpen(false);
      return;
    }
    if (!validateSellerRows(sellerRows, setFormErrors)) {
      setSubmitError('Fill Seller System Sales for every seller before confirming.');
      return;
    }
    setDoubleCheckOpen(true);
  };

  const handleFinalConfirmClose = () => {
    confirmMutation.mutate({
      date: todayKey,
      storeInfoId,
      vendorId,
      storeName,
      sellers: sellerRows.map((row) => ({
        sellerId: row.sellerId,
        sellerName: row.sellerName,
        fmdSystemSales: Number(row.fmdSystemSales) || 0,
        sellerSystemSales: Number(row.sellerSystemSales) || 0,
      })),
    });
  };

  const handleRequestReopen = () => {
    setSubmitError('');
    if (String(reopenReason || '').trim().length < 3) {
      setSubmitError('Enter a reason for reopen (at least 3 characters).');
      return;
    }
    requestMutation.mutate({
      date: todayKey,
      storeInfoId,
      vendorId,
      reason: String(reopenReason).trim(),
    });
  };

  const handleReview = (action) => {
    setSubmitError('');
    reviewMutation.mutate({
      date: todayKey,
      storeInfoId,
      vendorId,
      storeName,
      action,
    });
  };

  const closeMainModal = () => {
    if (confirmMutation.isLoading) return;
    setDoubleCheckOpen(false);
    formSeededRef.current = false;
    setModalOpen(false);
  };

  if (!canClose) return null;

  const busy = confirmMutation.isLoading || requestMutation.isLoading || reviewMutation.isLoading;

  return (
    <>
      <div className="flex flex-col items-end gap-1">
        <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 shadow-sm">
          <div className="text-right leading-tight">
            <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Store Close</div>
            <div className="max-w-[180px] truncate text-[11px] text-slate-500" title={storeName || ''}>
              {storeName || (storeInfoId ? `Store #${storeInfoId}` : 'Select store')}
            </div>
            <div className={`text-sm font-semibold ${isClosed ? 'text-rose-600' : 'text-emerald-600'}`}>
              {!storeInfoId || !vendorId
                ? 'Select store'
                : (closeStatusLoading ? 'Checking…' : (isClosed ? 'Shutter down' : 'Open'))}
            </div>
            {isClosed && closedByName ? (
              <div
                className="mt-0.5 max-w-[180px] truncate text-[11px] font-medium text-slate-600"
                title={closedAtLabel ? `Closed by ${closedByName} · ${closedAtLabel}` : `Closed by ${closedByName}`}
              >
                by {closedByName}
              </div>
            ) : null}
          </div>
          <button
            type="button"
            role="switch"
            aria-checked={toggleOn}
            aria-label="Store close toggle"
            onClick={handleToggleClick}
            disabled={closeStatusLoading || !storeInfoId || !vendorId || busy}
            className={`relative inline-flex h-8 w-14 shrink-0 cursor-pointer items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
              toggleOn ? 'bg-emerald-500' : 'bg-rose-500'
            }`}
            title={
              !storeInfoId || !vendorId
                ? 'Select a store in the header first'
                : (isClosed
                  ? (closedByName
                    ? `Closed by ${closedByName}${closedAtLabel ? ` · ${closedAtLabel}` : ''}. Click to view.`
                    : 'View today\'s close entry for this store')
                  : 'Turn off to close shutter for this store today')
            }
          >
            <span
              className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition ${
                toggleOn ? 'translate-x-7' : 'translate-x-1'
              }`}
            />
          </button>
        </div>

        {isClosed && hasPendingReopen && canEditReopen ? (
          <button
            type="button"
            onClick={openReviewModal}
            className="animate-pulse rounded-lg border border-amber-300 bg-amber-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-amber-800 shadow-sm hover:bg-amber-100"
          >
            Reopen request · Review
          </button>
        ) : null}

        {isClosed && hasPendingReopen && !canEditReopen ? (
          <div className="max-w-[220px] rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800">
            Reopen requested — waiting for admin
          </div>
        ) : null}

        {isClosed && !hasPendingReopen && canClose ? (
          <button
            type="button"
            onClick={() => {
              setSubmitError('');
              setReopenReason('');
              setRequestModalOpen(true);
            }}
            className="text-[11px] font-semibold text-slate-600 underline-offset-2 hover:text-rose-700 hover:underline"
          >
            Request reopen
          </button>
        ) : null}

      </div>

      <Modal isOpen={modalOpen} onClose={busy ? undefined : closeMainModal} size="xl">
        <div className="px-5 py-5 sm:px-6">
          <h3 className="text-lg font-semibold text-slate-900">
            {isClosed ? 'Store Close Summary' : 'Close Store Shutter'}
          </h3>
          <p className="mt-1 text-sm text-slate-600">
            {storeName || 'Selected store'} · Sales for {salesDateLabel}.
          </p>
          {isClosed ? (
            <div className="mt-3 rounded-lg border border-rose-100 bg-rose-50 px-3 py-2.5 text-sm text-rose-900">
              <span className="font-semibold">Closed by:</span>{' '}
              {closedByName || 'Unknown staff'}
              {closedAtLabel ? (
                <span className="mt-0.5 block text-xs font-medium text-rose-700/80">{closedAtLabel}</span>
              ) : null}
            </div>
          ) : null}
          {isClosed && hasPendingReopen ? (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-900">
              <span className="font-semibold">Reopen requested by:</span>{' '}
              {reopenRequest?.requestedByName || 'Staff'}
              <span className="mt-1 block text-xs">{reopenRequest?.reason}</span>
            </div>
          ) : null}

          <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">Seller Name</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700">FMD System Sales</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700">Seller System Sales</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {sellerRows.map((row, index) => {
                  const fmd = Number(row.fmdSystemSales) || 0;
                  const sellerNumeric = Number(row.sellerSystemSales);
                  const sellerValue = Number.isFinite(sellerNumeric) ? sellerNumeric : 0;
                  const variance = fmd - sellerValue;
                  const errorKey = `seller_${index}`;
                  return (
                    <tr key={`${row.sellerId || 'seller'}-${index}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">{row.sellerName}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {formatCurrencyAmount(fmd)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {isClosed ? (
                          <span className="tabular-nums text-slate-700">{formatCurrencyAmount(sellerValue)}</span>
                        ) : (
                          <div>
                            <input
                              type="text"
                              inputMode="decimal"
                              autoComplete="off"
                              className="input w-40 rounded-lg text-right"
                              value={row.sellerSystemSales}
                              onChange={(event) => updateSellerSystemSales(index, event.target.value)}
                              placeholder="Enter amount"
                            />
                            {formErrors[errorKey] ? (
                              <span className="mt-1 block text-xs text-danger-600">{formErrors[errorKey]}</span>
                            ) : null}
                          </div>
                        )}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${variance === 0 ? 'text-slate-700' : 'text-amber-700'}`}>
                        {formatCurrencyAmount(variance)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-3 py-2 text-slate-900">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                    {formatCurrencyAmount(totals.fmdSystemSales)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                    {formatCurrencyAmount(totals.sellerSystemSales)}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${totals.variance === 0 ? 'text-slate-900' : 'text-amber-700'}`}>
                    {formatCurrencyAmount(totals.variance)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {!isClosed && !closeStatusLoading && sellerRows.length === 0 ? (
            <p className="mt-3 text-sm text-amber-700">
              {closeStatusError
                ? (closeStatusQueryError?.response?.data?.message
                  || closeStatusQueryError?.message
                  || 'Could not load sellers. Run SQL script 075 on this database, then reopen.')
                : 'No seller rows loaded. Select a store in the header, then reopen Close Store Shutter.'}
            </p>
          ) : null}

          {submitError && modalOpen && !requestModalOpen && !reviewModalOpen ? (
            <p className="mt-3 text-sm text-danger-600">{submitError}</p>
          ) : null}

          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={closeMainModal}
              disabled={busy}
            >
              {isClosed ? 'Close' : 'Cancel'}
            </button>
            {!isClosed ? (
              <button
                type="button"
                className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
                onClick={handleConfirm}
                disabled={busy || sellerRows.length === 0}
              >
                Confirm Close
              </button>
            ) : null}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={doubleCheckOpen}
        onClose={busy ? undefined : () => setDoubleCheckOpen(false)}
        size="sm"
        zIndexClass="z-[60]"
      >
        <div className="px-5 py-5 sm:px-6">
          <h3 className="text-lg font-semibold text-slate-900">
            Double-check store close
          </h3>
          <p className="mt-2 text-sm text-slate-600">
            Close shutter for <span className="font-semibold text-slate-900">{storeName || 'this store'}</span> on{' '}
            <span className="font-semibold text-slate-900">{salesDateLabel}</span>?
          </p>
          <div className="mt-4 space-y-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-sm">
            <div className="flex justify-between gap-3">
              <span className="text-slate-500">FMD System Sales</span>
              <span className="font-semibold tabular-nums text-slate-900">{formatCurrencyAmount(totals.fmdSystemSales)}</span>
            </div>
            <div className="flex justify-between gap-3">
              <span className="text-slate-500">Seller System Sales</span>
              <span className="font-semibold tabular-nums text-slate-900">{formatCurrencyAmount(totals.sellerSystemSales)}</span>
            </div>
            <div className="flex justify-between gap-3 border-t border-slate-200 pt-2">
              <span className="text-slate-500">Variance</span>
              <span className={`font-semibold tabular-nums ${totals.variance === 0 ? 'text-slate-900' : 'text-amber-700'}`}>
                {formatCurrencyAmount(totals.variance)}
              </span>
            </div>
          </div>
          <p className="mt-3 text-xs text-slate-500">
            After close, corrections require a recorded reopen request and admin approval.
          </p>
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
              onClick={() => setDoubleCheckOpen(false)}
              disabled={busy}
            >
              Go Back
            </button>
            <button
              type="button"
              className="rounded-lg bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:opacity-50"
              onClick={handleFinalConfirmClose}
              disabled={busy}
            >
              {confirmMutation.isLoading
                ? 'Saving…'
                : 'Yes, Close Shutter'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={requestModalOpen}
        onClose={busy ? undefined : () => setRequestModalOpen(false)}
        size="sm"
        zIndexClass="z-[60]"
      >
        <div className="px-5 py-5 sm:px-6">
          <h3 className="text-lg font-semibold text-slate-900">Request reopen</h3>
          <p className="mt-2 text-sm text-slate-600">
            Ask admin to reopen <span className="font-semibold">{storeName || 'this store'}</span> for {salesDateLabel}.
          </p>
          <label className="mt-4 block text-xs font-semibold uppercase tracking-wide text-slate-500">
            Reason
          </label>
          <textarea
            className="input mt-1 min-h-[96px] w-full rounded-lg"
            value={reopenReason}
            onChange={(event) => setReopenReason(event.target.value)}
            placeholder="Explain the mistake (e.g. wrong seller sales entered)"
            maxLength={1000}
          />
          {submitError && requestModalOpen ? (
            <p className="mt-2 text-sm text-danger-600">{submitError}</p>
          ) : null}
          <div className="mt-5 flex justify-end gap-3">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setRequestModalOpen(false)}
              disabled={busy}
            >
              Cancel
            </button>
            <button
              type="button"
              className="rounded-lg bg-amber-600 px-4 py-2 text-sm font-semibold text-white hover:bg-amber-700 disabled:opacity-50"
              onClick={handleRequestReopen}
              disabled={busy}
            >
              {requestMutation.isLoading ? 'Sending…' : 'Send request'}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={reviewModalOpen}
        onClose={busy ? undefined : () => setReviewModalOpen(false)}
        size="xl"
        zIndexClass="z-[60]"
      >
        <div className="px-5 py-5 sm:px-6">
          <h3 className="text-lg font-semibold text-slate-900">Review reopen request</h3>
          <p className="mt-1 text-sm text-slate-600">
            {storeName || 'Selected store'} · {salesDateLabel}
          </p>

          {hasPendingReopen ? (
            <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-sm text-amber-950">
              <div>
                <span className="font-semibold">Requested by:</span> {reopenRequest?.requestedByName || 'Staff'}
              </div>
              {reopenRequest?.requestedAt ? (
                <div className="text-xs text-amber-800/80">{new Date(reopenRequest.requestedAt).toLocaleString()}</div>
              ) : null}
              <div className="mt-2">
                <span className="font-semibold">Reason:</span> {reopenRequest?.reason || '—'}
              </div>
            </div>
          ) : null}

          <div className="mt-4 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-slate-700">Seller Name</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700">FMD System Sales</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700">Seller System Sales</th>
                  <th className="px-3 py-2 text-right font-semibold text-slate-700">Variance</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {reviewSellerRows.map((row, index) => {
                  const fmd = Number(row.fmdSystemSales) || 0;
                  const sellerNumeric = Number(row.sellerSystemSales);
                  const sellerValue = Number.isFinite(sellerNumeric) ? sellerNumeric : 0;
                  const variance = fmd - sellerValue;
                  return (
                    <tr key={`review-${row.sellerId || 'seller'}-${index}`}>
                      <td className="px-3 py-2 font-medium text-slate-900">{row.sellerName}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {formatCurrencyAmount(fmd)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-700">
                        {formatCurrencyAmount(sellerValue)}
                      </td>
                      <td className={`px-3 py-2 text-right tabular-nums ${variance === 0 ? 'text-slate-700' : 'text-amber-700'}`}>
                        {formatCurrencyAmount(variance)}
                      </td>
                    </tr>
                  );
                })}
                <tr className="bg-slate-50 font-semibold">
                  <td className="px-3 py-2 text-slate-900">Total</td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                    {formatCurrencyAmount(reviewTotals.fmdSystemSales)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums text-slate-900">
                    {formatCurrencyAmount(reviewTotals.sellerSystemSales)}
                  </td>
                  <td className={`px-3 py-2 text-right tabular-nums ${reviewTotals.variance === 0 ? 'text-slate-900' : 'text-amber-700'}`}>
                    {formatCurrencyAmount(reviewTotals.variance)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {submitError && reviewModalOpen ? (
            <p className="mt-3 text-sm text-danger-600">{submitError}</p>
          ) : null}

          <div className="mt-5 flex flex-wrap justify-end gap-3">
            <button
              type="button"
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
              onClick={() => setReviewModalOpen(false)}
              disabled={busy}
            >
              Cancel
            </button>
            {hasPendingReopen ? (
              <button
                type="button"
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                onClick={() => handleReview('reject')}
                disabled={busy}
              >
                Reject request
              </button>
            ) : null}
            <button
              type="button"
              className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-50"
              onClick={() => handleReview('approve')}
              disabled={busy}
            >
              {reviewMutation.isLoading ? 'Saving…' : 'Approve & reopen'}
            </button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default StoreCloseControl;
